import { useContext, useState, useEffect } from 'react';
import { useSearchSettings } from '../contexts/SearchSettingsContext';
import { helpContent, HelpContentKey } from '../constants/helpContent';

export const useHelpContent = () => {
  const { settings, updateSettings } = useSearchSettings();

  // Set data-help-mode attribute on body when help mode changes
  useEffect(() => {
    if (settings.helpMode) {
      document.body.setAttribute('data-help-mode', 'true');
    } else {
      document.body.removeAttribute('data-help-mode');
    }
  }, [settings.helpMode]);

  const getHelpText = (key: HelpContentKey): string => {
    const content = helpContent[key];
    if (!content) return '';
    
    return settings.helpMode ? content.detailed : content.simple;
  };

  const getHelpTextForState = (key: HelpContentKey, isEnabled: boolean): string => {
    const enabledKey = key as HelpContentKey;
    const disabledKey = `${key}-disabled` as HelpContentKey;
    
    const contentKey = isEnabled ? enabledKey : disabledKey;
    const content = helpContent[contentKey];
    
    if (!content) {
      // Fallback to the original key if disabled version doesn't exist
      const fallbackContent = helpContent[enabledKey];
      return fallbackContent ? (settings.helpMode ? fallbackContent.detailed : fallbackContent.simple) : '';
    }
    
    return settings.helpMode ? content.detailed : content.simple;
  };

  const getTooltipText = (key: HelpContentKey): string => {
    const content = helpContent[key];
    if (!content) return '';
    
    // If detailed tooltip is enabled, show both short and detailed
    if (settings.helpMode) {
      return `${content.simple}\n\n${content.detailed}`;
    }
    
    // If not detailed, then if short tooltip is enabled, show short
    if (settings.shortTooltips) {
      return content.simple;
    }
    
    // Otherwise don't show tooltip
    return '';
  };

  const getTooltipTextForState = (key: HelpContentKey, isEnabled: boolean): string => {
    const enabledKey = key as HelpContentKey;
    const disabledKey = `${key}-disabled` as HelpContentKey;
    
    const contentKey = isEnabled ? enabledKey : disabledKey;
    const content = helpContent[contentKey];
    
    if (!content) {
      // Fallback to the original key if disabled version doesn't exist
      const fallbackContent = helpContent[enabledKey];
      if (!fallbackContent) return '';
      
      // For internal dialogs, show detailed tooltips when help mode is enabled
      if (settings.helpMode) {
        return fallbackContent.detailed;
      }
      
      // If not detailed, then if short tooltip is enabled, show short
      if (settings.shortTooltips) {
        return fallbackContent.simple;
      }
      
      // Otherwise don't show tooltip
      return '';
    }
    
    // For internal dialogs, show detailed tooltips when help mode is enabled
    if (settings.helpMode) {
      return content.detailed;
    }
    
    // If not detailed, then if short tooltip is enabled, show short
    if (settings.shortTooltips) {
      return content.simple;
    }
    
    // Otherwise don't show tooltip
    return '';
  };

  const getMenuTooltipTextForState = (key: HelpContentKey, isEnabled: boolean): string => {
    const enabledKey = key as HelpContentKey;
    const disabledKey = `${key}-disabled` as HelpContentKey;
    
    const contentKey = isEnabled ? enabledKey : disabledKey;
    const content = helpContent[contentKey];
    
    if (!content) {
      // Fallback to the original key if disabled version doesn't exist
      const fallbackContent = helpContent[enabledKey];
      if (!fallbackContent) return '';
      
      // For menus, show detailed tooltips when help mode is enabled
      if (settings.helpMode) {
        return fallbackContent.detailed;
      }
      
      // If not detailed, then if short tooltip is enabled, show short
      if (settings.shortTooltips) {
        return fallbackContent.simple;
      }
      
      // Otherwise don't show tooltip
      return '';
    }
    
    // For menus, show detailed tooltips when help mode is enabled
    if (settings.helpMode) {
      return content.detailed;
    }
    
    // If not detailed, then if short tooltip is enabled, show short
    if (settings.shortTooltips) {
      return content.simple;
    }
    
    // Otherwise don't show tooltip
    return '';
  };

  const getToolbarTooltipText = (key: HelpContentKey): string => {
    const content = helpContent[key];
    if (!content) return '';
    
    // For toolbar icons, always show combined tooltips when help mode is enabled
    if (settings.helpMode) {
      return `${content.simple}\n\n${content.detailed}`;
    }
    
    // If not detailed, then if short tooltip is enabled, show short
    if (settings.shortTooltips) {
      return content.simple;
    }
    
    // Otherwise don't show tooltip
    return '';
  };

  const getToolbarTooltipTextForState = (key: HelpContentKey, isEnabled: boolean): string => {
    const enabledKey = key as HelpContentKey;
    const disabledKey = `${key}-disabled` as HelpContentKey;
    
    const contentKey = isEnabled ? enabledKey : disabledKey;
    const content = helpContent[contentKey];
    
    if (!content) {
      // Fallback to the original key if disabled version doesn't exist
      const fallbackContent = helpContent[enabledKey];
      if (!fallbackContent) return '';
      
      // For toolbar icons, always show combined tooltips when help mode is enabled
      if (settings.helpMode) {
        return `${fallbackContent.simple}\n\n${fallbackContent.detailed}`;
      }
      
      // If not detailed, then if short tooltip is enabled, show short
      if (settings.shortTooltips) {
        return fallbackContent.simple;
      }
      
      // Otherwise don't show tooltip
      return '';
    }
    
    // For toolbar icons, always show combined tooltips when help mode is enabled
    if (settings.helpMode) {
      return `${content.simple}\n\n${content.detailed}`;
    }
    
    // If not detailed, then if short tooltip is enabled, show short
    if (settings.shortTooltips) {
      return content.simple;
    }
    
    // Otherwise don't show tooltip
    return '';
  };

  const shouldShowTooltip = (): boolean => {
    return settings.helpMode || settings.shortTooltips;
  };

  return {
    getHelpText,
    getHelpTextForState,
    getTooltipText,
    getTooltipTextForState,
    getMenuTooltipTextForState,
    getToolbarTooltipText,
    getToolbarTooltipTextForState,
    shouldShowTooltip,
    helpMode: settings.helpMode,
    shortTooltips: settings.shortTooltips
  };
}; 